import { useState, useRef, useCallback } from 'react';
import type { KeyboardEvent, ChangeEvent } from 'react';

interface TickerTagInputProps {
  /** Comma-separated ticker string (e.g., "AAPL,NVDA,TSLA") */
  value: string;
  /** Called with updated comma-separated string */
  onChange: (value: string) => void;
  placeholder?: string;
  /** Compact mode for toolbar usage (smaller tags, inline layout) */
  compact?: boolean;
}

/** Parse comma-separated string into cleaned, deduplicated ticker array */
function parseTickers(value: string): string[] {
  if (!value.trim()) return [];
  return [
    ...new Set(
      value
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter((s) => s.length > 0)
    ),
  ];
}

export function TickerTagInput({
  value,
  onChange,
  placeholder = 'Add ticker...',
  compact = false,
}: TickerTagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const tickers = parseTickers(value);

  const addTicker = useCallback(
    (raw: string) => {
      const ticker = raw.trim().toUpperCase();
      if (!ticker || tickers.includes(ticker)) return;
      const updated = [...tickers, ticker].join(',');
      onChange(updated);
    },
    [tickers, onChange]
  );

  const removeTicker = useCallback(
    (ticker: string) => {
      const updated = tickers.filter((t) => t !== ticker).join(',');
      onChange(updated);
    },
    [tickers, onChange]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const trimmed = inputValue.trim();

    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
      if (trimmed) {
        e.preventDefault();
        // Handle pasted multi-ticker input like "AAPL NVDA TSLA"
        const parts = trimmed.split(/[\s,]+/);
        for (const part of parts) {
          addTicker(part);
        }
        setInputValue('');
      } else if (e.key === 'Tab') {
        // Allow normal tab behavior when input is empty
        return;
      }
    } else if (e.key === ' ') {
      if (trimmed) {
        e.preventDefault();
        addTicker(trimmed);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && tickers.length > 0) {
      removeTicker(tickers[tickers.length - 1]);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // If user pastes something with commas or spaces, split immediately
    if (raw.includes(',') || raw.includes(' ')) {
      const parts = raw.split(/[\s,]+/).filter((s) => s.trim().length > 0);
      for (const part of parts) {
        addTicker(part);
      }
      setInputValue('');
    } else {
      setInputValue(raw.toUpperCase());
    }
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const clearAll = () => {
    onChange('');
    setInputValue('');
    inputRef.current?.focus();
  };

  if (compact) {
    return (
      <div
        // Narrower than it was (was 140–300). This variant is used only in the
        // Wiseguy toolbar, where it sits among several other controls and was
        // taking more room than a two- or three-ticker filter needs. The
        // non-compact branch below is unchanged and still serves the modal.
        className="relative flex items-center flex-wrap gap-1 min-w-[120px] max-w-[250px] min-h-[26px] pl-7 pr-6 py-0.5 bg-dark-surface border border-dark-border rounded cursor-text focus-within:border-accent-yellow"
        onClick={handleContainerClick}
      >
        {/* Search icon */}
        <svg
          className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {tickers.map((ticker) => (
          <span
            key={ticker}
            className="inline-flex items-center gap-0.5 px-1.5 py-0 text-[10px] font-medium bg-accent-yellow/15 text-accent-yellow border border-accent-yellow/25 rounded"
          >
            {ticker}
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeTicker(ticker);
              }}
              className="text-accent-yellow/60 hover:text-accent-yellow ml-0.5"
            >
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={tickers.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[40px] bg-transparent text-xs text-white placeholder-gray-500 focus:outline-none"
          style={{ maxWidth: tickers.length > 0 ? '60px' : undefined }}
        />
        {/* Clear all button */}
        {(tickers.length > 0 || inputValue) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  // Full-size variant (for preset modal)
  return (
    <div
      className="flex items-center flex-wrap gap-1.5 min-h-[38px] px-3 py-1.5 bg-dark-bg border border-dark-border rounded cursor-text focus-within:border-accent-yellow"
      onClick={handleContainerClick}
    >
      {tickers.map((ticker) => (
        <span
          key={ticker}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-accent-yellow/15 text-accent-yellow border border-accent-yellow/25 rounded"
        >
          {ticker}
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeTicker(ticker);
            }}
            className="text-accent-yellow/60 hover:text-accent-yellow"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={tickers.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[60px] bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
      />
    </div>
  );
}
